import { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../services/ApiService';
import { toBase64 } from '../services/toBase64';
import getUser from "../services/decodeJWT";
import { genderOptions, languageOptions, countryOptions } from '../constants/config';
import Nav from '../components/Nav';
import Loading from '../components/Loading';

const AddNewClone = () => {
    const navigate = useNavigate();
    const currentUser = getUser();

    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(false);

    const [clonesList, setClonesList] = useState([]);

    const [showWarning, setShowWarning] = useState(false);
    const [conflictCloneName, setConflictCloneName] = useState('');

    const [cloneData, setCloneData] = useState({
        name: '',
        surname: '',
        birthday: '',
        gender: '',
        language: '',
        spouse: [],
        child_of: [],
        city: '',
        country: '',
        description: '',
        picture: {},
        cover: {},
    });

    const getClones = useCallback(async () => {
        try {
            const response = await ApiService.get(`/cms/clones?limit=100&author=${currentUser?._id}`);
            setClonesList(response.contents || []);
        } catch (err) {
            console.error("Error during getClones:", err);
            setClonesList([]);
        }
    }, [currentUser?._id]);

    useEffect(() => {
        getClones();
    }, [getClones]);


    const handleInputChange = (field, value) => {
        setCloneData((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleSpouseSelection = (cloneId) => {
        setCloneData((prevState) => ({
            ...prevState,
            spouse: [cloneId] || [],
        }));
        validateSelections();
    };

    const handleParentSelection = (selectedOptions) => {
        const selectedParentIds = selectedOptions.map(option => option.value);
        setCloneData((prevState) => ({
            ...prevState,
            child_of: selectedParentIds,
        }));
        validateSelections();
    };

    const validateSelections = () => {
        const { spouse, child_of } = cloneData;

        if (spouse.length > 0 && child_of.includes(spouse[0])) {
            setShowWarning(true);
            const conflictClone = spouseOptions.find(option => option.value === spouse[0]);
            setConflictCloneName(conflictClone?.label || "");
        } else {
            setShowWarning(false);
            setConflictCloneName("");
        }
    };


    const handleImageChange = async (field, file) => {
        if (!file) return;

        const base64 = await toBase64(file);

        setCloneData((prevState) => ({
            ...prevState,
            [field]: {
                name: file?.name,
                media: base64
            },
            [field]: {
                name: file?.name,
                media: base64
            },
        }));
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            await ApiService.post('/cms/clones', { data: cloneData });
            toast.success('Clone created successfully!', {
                position: 'top-center',
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            setDisabled(true);
            setTimeout(() => {
                navigate("/clone-list");
            }, 2200);
        } catch (error) {
            console.error('Error creating clone:', error);
            setLoading(false);
            toast.error('Failed to create the clone. Please try again.', {
                position: 'top-center',
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setLoading(false);
        }
    };


    const spouseOptions = clonesList?.map(item => ({
        value: item._id,
        label: `${item.data.name} ${item.data.surname}`
    }));

    const parentOptions = clonesList?.map(item => ({
        value: item._id,
        label: `${item.data.name} ${item.data.surname}`,
    }));

    useEffect(() => {
        validateSelections()
    }, [cloneData])

    return (
        <div className="tyn-root add-clone clone">
            <Loading status={loading} blur={true}>
                <Nav />
                <div className="tyn-content tyn-add-clone">
                    <div className="tyn-main tyn-content-inner" id="tynMain">
                        <div className="container">
                            <div className="header d-flex align-items-center justify-content-between px-5 py-4" style={{ background: 'white', borderRadius: '1rem' }}>
                                <div>
                                    <h3>Create a New Clone</h3>
                                </div>
                                <div className="buttons d-flex">
                                    <button className="btn btn-primary me-2" onClick={handleSave} aria-label="Add new clone">Add</button>
                                    <Link className="btn btn-light" to={'/clone-list'} aria-label="Cancel and back to clone list">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                                            <path fillRule="evenodd" stroke="currentColor" strokeWidth="0.3" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                                        </svg>
                                        Clone List
                                    </Link>
                                </div>
                                <div className="back-btn-mobile" style={{ display: 'none' }}>
                                    <Link className="btn btn-light" to={'/clone-list'} aria-label="Back to list">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                                            <path fillRule="evenodd" stroke="currentColor" strokeWidth="0.3" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                                        </svg>
                                    </Link>
                                </div>
                            </div>

                            <div className="tyn-profile">
                                <div className="tyn-profile-head my-4">
                                    <div className="tyn-profile-cover new-cover">
                                        <img className="tyn-profile-cover-image" src={cloneData.cover.media || 'images/cover/banner-default.png'} alt="Cover Default" />

                                        {/* <!-- desktop --> */}
                                        <label htmlFor="cover-upload" className="upload-icon-label">
                                            Change Cover
                                            <input type="file" accept="image/*" id="cover-upload" className="cover-upload-input" onChange={(e) => handleImageChange('cover', e.target.files[0])} />
                                        </label>

                                        {/* <!--  mobile --> */}
                                        <label htmlFor="cover-upload" className="upload-icon-label mobile-upload">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-upload" viewBox="0 0 16 16">
                                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z" />
                                            </svg>
                                        </label>
                                    </div>


                                    <div className="tyn-profile-info">
                                        <div className="tyn-media-group align-items-start">
                                            <div className="tyn-media tyn-media-bordered tyn-size-4xl tyn-profile-avatar">
                                                <img src={cloneData.picture.media || 'images/avatar/avatar-default.jpg'} alt="Avatar Default" className="avatar-image" />

                                                {/* <!-- desktop --> */}
                                                <label htmlFor="avatar-upload" className="avatar-upload-label">
                                                    Change
                                                    <input type="file" accept="image/*" id="avatar-upload" className="avatar-upload-input" onChange={(e) => handleImageChange('picture', e.target.files[0])} />
                                                </label>

                                                {/* <!--  mobile --> */}
                                                <label htmlFor="avatar-upload" className="avatar-upload-label mobile-upload">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-upload" viewBox="0 0 16 16">
                                                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                        <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z" />
                                                    </svg>
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                <div className="tyn-profile-details">
                                    <div className="tab-content">
                                        <div className="tab-pane show active" id="profile-edit" tabIndex="0">
                                            <div className="row gy-5">
                                                <div className="col-12">
                                                    <div className="row gy-4">
                                                        <div className="col-lg-3">
                                                            <h6>Personal Information</h6>
                                                            <div className="tyn-subtext">Edit personal info</div>
                                                        </div>
                                                        <div className="col-lg-9">
                                                            <div className="row g-gs">
                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="name">First Name *</label>
                                                                        <div className="form-control-wrap">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                id="name"
                                                                                placeholder="Your Name"
                                                                                value={cloneData.name}
                                                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                                                required
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="surname">Last Name *</label>
                                                                        <div className="form-control-wrap">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                id="surname"
                                                                                placeholder="Your Last Name"
                                                                                value={cloneData.surname}
                                                                                onChange={(e) => handleInputChange('surname', e.target.value)}
                                                                                required
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="birthday">Birthday *</label>
                                                                        <div className="form-control-wrap" id='birthday'>
                                                                            <input
                                                                                type="date"
                                                                                className="form-control"
                                                                                id="birthday"
                                                                                value={cloneData.birthday}
                                                                                onChange={(e) => handleInputChange('birthday', e.target.value)}
                                                                                required
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="city">City of birth</label>
                                                                        <div className="form-control-wrap">
                                                                            <input type="text" className="form-control" id="city" placeholder="City" value={cloneData.city} onChange={(e) => handleInputChange('city', e.target.value)} />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="country">Country</label>
                                                                        <div className="form-control-wrap">
                                                                            <Select
                                                                                id="country"
                                                                                value={countryOptions.find(option => option.value === cloneData.country)}
                                                                                onChange={(selectedOption) => handleInputChange('country', selectedOption?.value || '')}
                                                                                options={countryOptions}
                                                                                placeholder="Select Country"
                                                                                isClearable
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="gender">Gender</label>
                                                                        <div className="form-control-wrap">
                                                                            <Select
                                                                                id="gender"
                                                                                value={genderOptions.find(option => option.value === cloneData.gender)}
                                                                                onChange={(selectedOption) => handleInputChange('gender', selectedOption?.value || '')}
                                                                                options={genderOptions}
                                                                                placeholder="Select Gender"
                                                                                isClearable
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="spouse">Spouse</label>
                                                                        <div className="form-control-wrap">
                                                                            <Select
                                                                                id="spouse"
                                                                                value={spouseOptions.find(option => option.value === cloneData.spouse)}
                                                                                onChange={(selectedOption) => handleSpouseSelection(selectedOption?.value)}
                                                                                options={spouseOptions}
                                                                                placeholder="Select Spouse"
                                                                                isClearable
                                                                            />

                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="parents">Parents</label>
                                                                        <div className="form-control-wrap">
                                                                            <Select
                                                                                isMulti
                                                                                id="parents"
                                                                                name="parents"
                                                                                options={parentOptions}
                                                                                placeholder="Select Parents"
                                                                                value={parentOptions.filter(option => cloneData.child_of.includes(option.value))}
                                                                                onChange={(selectedOptions) => handleParentSelection(selectedOptions || [])}
                                                                            />
                                                                        </div>
                                                                        {showWarning && <div className="warning-text">
                                                                            <div className="row">
                                                                                <div className="col-12 d-flex align-items-center text">
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#E11D48" className="bi bi-exclamation-circle" viewBox="0 0 16 16">
                                                                                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                                                                                        <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                                                                                    </svg>
                                                                                    <p className='mb-0'>
                                                                                        <span>{conflictCloneName}</span> can't be both a spouse and a parent.
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        }
                                                                    </div>
                                                                </div>


                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="language">Language spoken</label>
                                                                        <div className="form-control-wrap">
                                                                            <Select
                                                                                id="language"
                                                                                value={languageOptions.find(option => option.value === cloneData.language)}
                                                                                onChange={(selectedOption) => handleInputChange('language', selectedOption?.value || '')}
                                                                                options={languageOptions}
                                                                                placeholder="Select Language"
                                                                                isClearable
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="description">General description</label>
                                                                        <textarea className="form-control" id="description" placeholder="Description..." rows="4" value={cloneData.description} onChange={(e) => handleInputChange('description', e.target.value)}></textarea>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12">
                                                    <div className="row">
                                                        <div className="d-flex justify-content-end buttons mt-4">
                                                            <button className="btn btn-primary me-2" disabled={disabled || showWarning} onClick={handleSave} aria-label="Add new clone">Add Clone</button>
                                                            <button className="btn btn-light" disabled={disabled} onClick={() => navigate('/clone-list')} aria-label="Cancel and back to clone list">Cancel</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Loading>
        </div>
    );
};

export default AddNewClone;
